import { ConfiguratorContext_ProgramDataFragment } from "src/fragments/circularFragments.generated";
import { Maybe } from "~generated/graphqlTypes";
import { PlanUtils_ProgramDataFragment } from "./plan.utils.generated";
import { pluralize } from "./string.utils";

type ProductOfferingProgramData = {
  minProgramData: Omit<PlanUtils_ProgramDataFragment, "id">;
  maxProgramData: Omit<PlanUtils_ProgramDataFragment, "id">;
};

/**
 * Given a Plan fragment, return a string representing the bedroom range.
 *
 * @returns
 * - "X Beds"
 * - "X-Y Beds"
 */
export function getPlanBedroomRangeString(productOffering: ProductOfferingProgramData): string {
  const { minProgramData, maxProgramData } = productOffering;
  if (minProgramData.bedrooms === maxProgramData.bedrooms) return `${minProgramData.bedrooms} Beds`;
  return `${minProgramData.bedrooms}-${maxProgramData.bedrooms} Beds`;
}

/**
 * Given a Plan fragment, return a string representing the bathroom range.
 * @returns
 * - "A.B Baths" if the full and half baths are the same
 * - "A.B-A.C Baths" if full baths are the same and the half baths are different
 * - "A.B-C.B Baths" if full baths are different and the half baths are the same
 * - "A.B-C.D Baths" if full and half baths are different
 */
export function getPlanBathroomRangeString(productOffering: ProductOfferingProgramData): string {
  const { minProgramData, maxProgramData } = productOffering;
  const minFullBathrooms = minProgramData?.fullBaths ?? 0;
  const maxFullBathrooms = maxProgramData?.fullBaths ?? 0;
  const minHalfBathrooms = minProgramData?.halfBaths ?? 0;
  const maxHalfBathrooms = maxProgramData?.halfBaths ?? 0;

  let rangeText = "";

  // If the full and half bathrooms are the same, no need to return a range. If showRange is false, we don't want to show a range.
  if (minFullBathrooms === maxFullBathrooms && minHalfBathrooms === maxHalfBathrooms) {
    rangeText += minFullBathrooms + minHalfBathrooms / 2;
  }
  // Otherwise, return a range.
  else {
    rangeText += `${minFullBathrooms + minHalfBathrooms / 2}-${maxFullBathrooms + maxHalfBathrooms / 2}`;
  }

  return rangeText + " Baths";
}

/**
 * Given a Plan Fragment, return a string representing the square footage range.
 * @returns
 * - "A Sq Ft" if the min and max sqft are the same or showRange is false
 * - "A-B Sq Ft" if the min and max sqft are different and showRange is true
 */
export function getPlanSqftRangeString(productOffering: ProductOfferingProgramData): string {
  const { minProgramData, maxProgramData } = productOffering;
  const minSqft = minProgramData?.sellableSqft ?? 0;
  const maxSqft = maxProgramData?.sellableSqft ?? 0;

  if (minSqft === maxSqft) return `${minSqft} Sq Ft`;
  return `${minSqft}-${maxSqft} Sq Ft`;
}

/**
 * Given a `CheckoutConfig.programData`, returns the program data totals.
 * @returns an object with the following properties:
 * - sqft: string
 * - bedrooms: string
 * - bathrooms: string
 */
export function getProgramDataStrings(programData: Maybe<ConfiguratorContext_ProgramDataFragment>) {
  const { bedrooms, fullBaths, halfBaths, sellableSqft } = programData ?? {};

  // Treat "half" baths as .5 baths so 2.5 baths is 2 full baths and 1 half bath.
  // But only show the decimal if it's not a whole number.
  const fullBathsCount = fullBaths ?? 0;
  const halfBathsCount = (halfBaths ?? 0) / 2;
  const totalBaths = fullBathsCount + halfBathsCount;
  const bathroomsString = Number.isInteger(totalBaths) ? totalBaths : totalBaths.toFixed(1);

  return {
    bedrooms: `${bedrooms ?? 0} ${pluralize(bedrooms ?? 0, "Bed")}`,
    bathrooms: `${bathroomsString} ${pluralize(totalBaths, "Bath")}`,
    sqft: `${(sellableSqft ?? 0).toLocaleString()} Sq Ft`,
  };
}
